// src/pages/Home.js
import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FaLeaf, FaChartLine, FaUsers, FaClock, FaCalendarAlt, FaCheck, FaArrowRight } from 'react-icons/fa';
import { useMobileDetector } from '../utils/deviceDetection';

// Mobile-optimized stat card with horizontal layout
const MobileStatCard = ({ icon, number, text }) => (
  <div className="bg-gradient-to-br from-surface to-gray-800 p-4 rounded-lg flex items-center border border-gray-700 shadow-lg mb-3">
    <div className="text-blue-400 mr-4">{icon}</div>
    <div>
      <h3 className="text-xl font-bold text-white">{number}</h3>
      <p className="text-blue-100 text-sm">{text}</p>
    </div>
  </div>
);

// Desktop stat card (vertical layout)
const StatCard = ({ icon, number, text }) => (
  <div className="bg-gradient-to-br from-surface to-gray-800 p-6 rounded-lg text-center border border-gray-700 shadow-lg hover:shadow-xl transition duration-300">
    <div className="text-blue-400">{icon}</div>
    <h3 className="text-2xl font-bold mt-2 text-white">{number}</h3>
    <p className="text-blue-100">{text}</p>
  </div>
);

// Mobile-optimized benefit card with strong value proposition
const MobileBenefitCard = ({ title, description }) => (
  <div className="bg-gradient-to-br from-surface to-gray-800 p-4 rounded-lg border border-gray-700 shadow-lg mb-3">
    <div className="flex items-start">
      <FaCheck className="text-green-400 mt-1 mr-2 flex-shrink-0" />
      <div>
        <h3 className="text-lg font-bold text-white">{title}</h3>
        <p className="text-blue-100 text-sm">{description}</p>
      </div>
    </div>
  </div>
);

// Mobile-optimized testimonial with more compact design
const MobileTestimonialCard = ({ quote, author, company, results }) => (
  <div className="bg-gradient-to-br from-surface to-gray-800 p-5 rounded-lg border border-gray-700 shadow-lg mb-4">
    <p className="text-blue-100 italic text-sm mb-2">"{quote}"</p>
    <div className="bg-blue-900/30 p-2 rounded-md mb-2">
      <p className="text-green-400 text-sm font-semibold">{results}</p>
    </div>
    <p className="font-semibold text-white text-sm">{author}</p>
    <p className="text-blue-200 text-xs">{company}</p>
  </div>
);

// Desktop testimonial card with results highlight
const TestimonialCard = ({ quote, author, company, results }) => (
  <div className="bg-gradient-to-br from-surface to-gray-800 p-8 rounded-lg border border-gray-700 shadow-lg hover:shadow-xl transition duration-300">
    <p className="text-blue-100 italic mb-4">"{quote}"</p>
    <div className="bg-blue-900/30 p-3 rounded-md mb-4">
      <p className="text-green-400 font-semibold">{results}</p>
    </div>
    <p className="font-semibold text-white">{author}</p>
    <p className="text-blue-200">{company}</p>
  </div>
);

const Home = () => {
  const isMobile = useMobileDetector();
  
  return (
    <div className="bg-gradient-to-b from-gray-900 to-background text-text min-h-screen">
      <Navbar />
      
      {/* Hero Section - Optimized for both mobile and desktop */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12 md:py-20">
          <div className="relative">
            <div className="absolute inset-0 blur-[100px] bg-gradient-to-r from-blue-500/30 to-purple-500/30 transform rotate-12"></div>
            <h1 className={`relative text-4xl ${isMobile ? '' : 'text-5xl sm:text-6xl md:text-7xl'} font-extrabold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400`}>
              Double Your Lawn Care Profits In 90 Days
            </h1>
          </div>
          <p className={`mt-3 mx-auto text-lg ${isMobile ? 'max-w-xs' : 'text-xl max-w-lg sm:text-2xl md:mt-5 md:max-w-2xl'} text-blue-100`}>
            Powerful software that helps lawn care businesses grow revenue, cut costs, and save 20+ hours every week
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/pricing"
              className="px-6 py-3 md:px-8 md:py-4 rounded-md bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold text-lg hover:from-blue-600 hover:to-blue-700 transform hover:scale-105 transition duration-200 shadow-lg hover:shadow-blue-500/50"
            >
              Start Free Trial <span className="ml-2">→</span>
            </Link>
            <Link
              to="/features"
              className="px-6 py-3 md:px-8 md:py-4 rounded-md bg-white/10 backdrop-blur-sm text-white font-medium hover:bg-white/20 transform hover:scale-105 transition duration-200"
            >
              See How It Works
            </Link>
          </div>
          <div className="mt-4 text-blue-300 text-sm">
            No credit card required • 14-day free trial • Cancel anytime
          </div>
        </div>

        {/* Stats Section - Renamed to Results Section */}
        <div className={`py-8 md:py-16`}>
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-6 md:mb-12 text-white">
            Real Results Our Users Are Getting
          </h2>
          
          {isMobile ? (
            // Mobile stats: vertical list with horizontal cards
            <div className="space-y-3">
              <MobileStatCard 
                icon={<FaChartLine className="text-green-400 text-3xl" />}
                number="32%"
                text="Average Revenue Increase"
              />
              <MobileStatCard 
                icon={<FaClock className="text-green-400 text-3xl" />}
                number="20+"
                text="Hours Saved Per Week"
              />
              <MobileStatCard 
                icon={<FaUsers className="text-green-400 text-3xl" />}
                number="40%"
                text="More Clients Serviced"
              />
              <MobileStatCard 
                icon={<FaLeaf className="text-green-400 text-3xl" />}
                number="28%"
                text="Reduced Operating Costs"
              />
            </div>
          ) : (
            // Desktop stats: grid layout
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <StatCard 
                icon={<FaChartLine className="text-green-400 text-4xl mx-auto" />}
                number="32%"
                text="Average Revenue Increase"
              />
              <StatCard 
                icon={<FaClock className="text-green-400 text-4xl mx-auto" />}
                number="20+"
                text="Hours Saved Per Week"
              />
              <StatCard 
                icon={<FaUsers className="text-green-400 text-4xl mx-auto" />}
                number="40%"
                text="More Clients Serviced"
              />
              <StatCard 
                icon={<FaLeaf className="text-green-400 text-4xl mx-auto" />}
                number="28%"
                text="Reduced Operating Costs"
              />
            </div>
          )}
        </div>

        {/* Benefits Section - Replaces Features Section */}
        <div className={`py-8 md:py-16`}>
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-6 md:mb-12 text-white">
            How You'll Transform Your Business
          </h2>
          
          {isMobile ? (
            // Mobile: Vertical list of benefit cards
            <div className="space-y-4">
              <MobileBenefitCard 
                title="Eliminate Scheduling Headaches"
                description="Our smart scheduling system cuts admin time by 80% and prevents double-bookings automatically."
              />
              <MobileBenefitCard 
                title="Reduce Fuel Costs by 35%"
                description="Optimize your team's routes with our GPS mapping to save time, fuel, and vehicle maintenance costs."
              />
              <MobileBenefitCard 
                title="Get Paid Faster, Every Time"
                description="Automated invoicing and payment reminders help you collect 94% of payments within 48 hours."
              />
              <MobileBenefitCard 
                title="Never Lose a Customer Again"
                description="Our customer management system helps you deliver exceptional service and increase retention by 58%."
              />
            </div>
          ) : (
            // Desktop: Grid layout
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-surface to-gray-800 p-8 rounded-lg border border-gray-700 shadow-lg hover:shadow-xl transition-all duration-200">
                <div className="flex items-start">
                  <div className="bg-blue-500/20 rounded-full p-3 mr-4">
                    <FaCalendarAlt className="text-blue-400 text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-3 text-white">Eliminate Scheduling Headaches</h3>
                    <p className="text-blue-100 mb-4">Our smart scheduling system cuts admin time by 80% and prevents double-bookings automatically.</p>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <FaCheck className="text-green-400 mt-1 mr-2" />
                        <span className="text-blue-100">Reduce no-shows by 75% with automated reminders</span>
                      </li>
                      <li className="flex items-start">
                        <FaCheck className="text-green-400 mt-1 mr-2" />
                        <span className="text-blue-100">Save 15+ hours weekly on administrative tasks</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-surface to-gray-800 p-8 rounded-lg border border-gray-700 shadow-lg hover:shadow-xl transition-all duration-200">
                <div className="flex items-start">
                  <div className="bg-blue-500/20 rounded-full p-3 mr-4">
                    <FaChartLine className="text-blue-400 text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-3 text-white">Reduce Fuel Costs by 35%</h3>
                    <p className="text-blue-100 mb-4">Optimize your team's routes with our GPS mapping to save time, fuel, and vehicle maintenance costs.</p>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <FaCheck className="text-green-400 mt-1 mr-2" />
                        <span className="text-blue-100">Complete 40% more jobs with the same team size</span>
                      </li>
                      <li className="flex items-start">
                        <FaCheck className="text-green-400 mt-1 mr-2" />
                        <span className="text-blue-100">Cut drive time between jobs in half</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-surface to-gray-800 p-8 rounded-lg border border-gray-700 shadow-lg hover:shadow-xl transition-all duration-200">
                <div className="flex items-start">
                  <div className="bg-blue-500/20 rounded-full p-3 mr-4">
                    <FaUsers className="text-blue-400 text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-3 text-white">Get Paid Faster, Every Time</h3>
                    <p className="text-blue-100 mb-4">Automated invoicing and payment reminders help you collect 94% of payments within 48 hours.</p>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <FaCheck className="text-green-400 mt-1 mr-2" />
                        <span className="text-blue-100">Reduce payment delays by 88%</span>
                      </li>
                      <li className="flex items-start">
                        <FaCheck className="text-green-400 mt-1 mr-2" />
                        <span className="text-blue-100">Automatically track and follow up on overdue invoices</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-surface to-gray-800 p-8 rounded-lg border border-gray-700 shadow-lg hover:shadow-xl transition-all duration-200">
                <div className="flex items-start">
                  <div className="bg-blue-500/20 rounded-full p-3 mr-4">
                    <FaClock className="text-blue-400 text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-3 text-white">Never Lose a Customer Again</h3>
                    <p className="text-blue-100 mb-4">Our customer management system helps you deliver exceptional service and increase retention by 58%.</p>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <FaCheck className="text-green-400 mt-1 mr-2" />
                        <span className="text-blue-100">Increase 5-star reviews by 70%</span>
                      </li>
                      <li className="flex items-start">
                        <FaCheck className="text-green-400 mt-1 mr-2" />
                        <span className="text-blue-100">Track and respond to customer needs instantly</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Testimonials Section - Enhanced with specific results */}
        <div className={`py-8 md:py-16`}>
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-6 md:mb-12 text-white">
            Lawn Care Owners Getting Results
          </h2>
          
          {isMobile ? (
            // Mobile: Compact vertical list
            <div className="space-y-4">
              <MobileTestimonialCard 
                quote="I used to work 80+ hours a week. Now I spend more time with my family while making more money."
                results="Revenue up 47% in 60 days"
                author="John Smith"
                company="Green Thumb Services"
              />
              <MobileTestimonialCard 
                quote="The route optimization alone has saved us thousands in fuel and labor costs every month."
                results="Cut operating costs by 32%"
                author="Sarah Johnson"
                company="Perfect Lawns LLC"
              />
              <MobileTestimonialCard 
                quote="We doubled our customer base in 3 months without adding any staff or working crazy hours."
                results="Added 53 new clients"
                author="Mike Wilson"
                company="Elite Lawn Care"
              />
            </div>
          ) : (
            // Desktop: Grid layout
            <div className="grid md:grid-cols-3 gap-8">
              <TestimonialCard 
                quote="I used to work 80+ hours a week. Now I spend more time with my family while making more money."
                results="Revenue up 47% in 60 days"
                author="John Smith"
                company="Green Thumb Services"
              />
              <TestimonialCard 
                quote="The route optimization alone has saved us thousands in fuel and labor costs every month."
                results="Cut operating costs by 32%"
                author="Sarah Johnson"
                company="Perfect Lawns LLC"
              />
              <TestimonialCard 
                quote="We doubled our customer base in 3 months without adding any staff or working crazy hours."
                results="Added 53 new clients"
                author="Mike Wilson"
                company="Elite Lawn Care"
              />
            </div>
          )}
        </div>

        {/* CTA Section - Fixed at bottom for mobile */}
        {isMobile ? (
          <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-blue-600 to-blue-700 p-4 shadow-lg z-40">
            <Link
              to="/pricing"
              className="block w-full py-3 rounded-md bg-white text-blue-600 font-bold text-center text-lg"
            >
              Start Your 14-Day Free Trial
            </Link>
          </div>
        ) : (
          <div className="py-16 text-center">
            <h2 className="text-3xl font-bold mb-6">Ready To Grow Your Lawn Care Business?</h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">
              Join hundreds of lawn care professionals saving time and increasing profits with our all-in-one business solution.
            </p>
            <Link
              to="/pricing"
              className="inline-block px-10 py-4 rounded-md bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold text-xl hover:from-blue-600 hover:to-blue-700 transform hover:scale-105 transition duration-200 shadow-lg"
            >
              Start Your 14-Day Free Trial <FaArrowRight className="inline ml-2" />
            </Link>
            <p className="mt-4 text-blue-300">
              No credit card required • Cancel anytime
            </p>
          </div>
        )}
        
        {/* Add padding at the bottom for mobile to account for fixed CTA */}
        {isMobile && <div className="h-20"></div>}
      </main>
      <Footer />
    </div>
  );
};

export default Home;

